import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, NgZone, OnDestroy } from '@angular/core';

export interface HotkeyRegistration {
  id: string;
  combo: string;
  handler: (event: KeyboardEvent) => void | Promise<void>;
  enabled?: () => boolean;
  allowInEditable?: boolean;
  preventDefault?: boolean;
  stopPropagation?: boolean;
  allowRepeat?: boolean;
}

interface ParsedHotkey {
  id: string;
  key: string;
  ctrl: boolean;
  alt: boolean;
  shift: boolean;
  handler: (event: KeyboardEvent) => void | Promise<void>;
  enabled?: () => boolean;
  allowInEditable: boolean;
  preventDefault: boolean;
  stopPropagation: boolean;
  allowRepeat: boolean;
}

@Injectable({ providedIn: 'root' })
export class HotkeyService implements OnDestroy {
  private readonly hotkeys = new Map<string, ParsedHotkey>();
  private listenerAttached = false;

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly zone: NgZone
  ) {}

  register(registration: HotkeyRegistration): () => void {
    if (!registration.id?.trim()) {
      throw new Error('Hotkey id is required.');
    }

    if (!registration.combo?.trim()) {
      throw new Error('Hotkey combo is required.');
    }

    this.hotkeys.set(registration.id, this.parseRegistration(registration));
    this.attachListener();

    return () => {
      this.unregister(registration.id);
    };
  }

  unregister(id: string): void {
    this.hotkeys.delete(id);

    if (this.hotkeys.size === 0) {
      this.detachListener();
    }
  }

  ngOnDestroy(): void {
    this.hotkeys.clear();
    this.detachListener();
  }

  private attachListener(): void {
    if (this.listenerAttached) {
      return;
    }

    this.zone.runOutsideAngular(() => {
      this.document.addEventListener('keydown', this.onKeydown, true);
    });

    this.listenerAttached = true;
  }

  private detachListener(): void {
    if (!this.listenerAttached) {
      return;
    }

    this.document.removeEventListener('keydown', this.onKeydown, true);
    this.listenerAttached = false;
  }

  private readonly onKeydown = (event: KeyboardEvent): void => {
    for (const hotkey of this.hotkeys.values()) {
      if (hotkey.enabled && !hotkey.enabled()) {
        continue;
      }

      if (!hotkey.allowRepeat && event.repeat) {
        continue;
      }

      if (!hotkey.allowInEditable && this.isEditableTarget(event.target)) {
        continue;
      }

      if (!this.matches(event, hotkey)) {
        continue;
      }

      if (hotkey.preventDefault) {
        event.preventDefault();
      }

      if (hotkey.stopPropagation) {
        event.stopPropagation();
      }

      this.zone.run(() => {
        void hotkey.handler(event);
      });

      return;
    }
  };

  private parseRegistration(registration: HotkeyRegistration): ParsedHotkey {
    const comboParts = registration.combo
      .toLowerCase()
      .split('+')
      .map(part => part.trim())
      .filter(Boolean);

    let key: string | undefined;
    let ctrl = false;
    let alt = false;
    let shift = false;

    for (const part of comboParts) {
      switch (part) {
        case 'ctrl':
        case 'control':
          ctrl = true;
          break;
        case 'alt':
        case 'option':
          alt = true;
          break;
        case 'shift':
          shift = true;
          break;
        default:
          key = this.normalizeKey(part);
          break;
      }
    }

    if (!key) {
      throw new Error(`Hotkey combo "${registration.combo}" must include a non-modifier key.`);
    }

    return {
      id: registration.id,
      key,
      ctrl,
      alt,
      shift,
      handler: registration.handler,
      enabled: registration.enabled,
      allowInEditable: registration.allowInEditable ?? false,
      preventDefault: registration.preventDefault ?? false,
      stopPropagation: registration.stopPropagation ?? false,
      allowRepeat: registration.allowRepeat ?? false
    };
  }

  private matches(event: KeyboardEvent, hotkey: ParsedHotkey): boolean {
    return event.ctrlKey === hotkey.ctrl
      && event.altKey === hotkey.alt
      && event.shiftKey === hotkey.shift
      && this.normalizeKey(event.key) === hotkey.key;
  }

  private normalizeKey(key: string): string {
    const normalized = key.toLowerCase();

    if (normalized === ' ') {
      return 'space';
    }

    if (normalized === 'esc') {
      return 'escape';
    }

    if (normalized === 'del') {
      return 'delete';
    }

    return normalized;
  }

  private isEditableTarget(target: EventTarget | null): boolean {
    const element = target as HTMLElement | null;
    if (!element) {
      return false;
    }

    const tagName = element.tagName?.toLowerCase();
    return element.isContentEditable || tagName === 'input' || tagName === 'textarea' || tagName === 'select';
  }
}